import { Switch } from 'components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { ListIcon, SearchIcon, XIcon } from 'components/icons';
import { t } from 'utils/getTranslations';
import { AuthorFilter } from './AuthorFilter';
import { AuditActionFilter, AuditTrailFilterState } from '@common/audit-trail/buildAuditTrailRsql';
import { EntityTypeFilter } from './EntityTypeFilter';
import { ChangedAtRange, TimeFilter } from './TimeFilter';
import { TOOLBAR_SHADOW } from './toolbarStyles';

const ACTION_SEGMENTS: { value: AuditActionFilter; labelKey: string }[] = [
    { value: 'All', labelKey: 'audit-trail.action-all' },
    { value: 'Created', labelKey: 'audit-trail.filter-created' },
    { value: 'Updated', labelKey: 'audit-trail.filter-updated' },
    { value: 'Deleted', labelKey: 'audit-trail.filter-deleted' },
];

export function AuditTrailToolbar({
    filters,
    entityTypes,
    onSearchChange,
    onActionChange,
    onEntityTypesChange,
    onAuthorIdsChange,
    onChangedAtRangeChange,
    onHideSystemChange,
    onClearTransaction,
}: {
    filters: AuditTrailFilterState;
    entityTypes: string[];
    onSearchChange: (search: string) => void;
    onActionChange: (action: AuditActionFilter) => void;
    onEntityTypesChange: (entityTypes: string[]) => void;
    onAuthorIdsChange: (authorIds: number[]) => void;
    onChangedAtRangeChange: (update: Partial<ChangedAtRange>) => void;
    onHideSystemChange: (hideSystem: boolean) => void;
    onClearTransaction: () => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2.5">
            <div
                className={`bg-paper flex h-10 min-w-50 flex-[1_1_240px] items-center gap-2 rounded-[10px] px-3 ${TOOLBAR_SHADOW}`}
            >
                <SearchIcon className="text-grey-400 size-4 flex-none" />
                <input
                    value={filters.search}
                    placeholder={t('audit-trail.search-placeholder')}
                    className="text-fg placeholder:text-grey-400 new:placeholder:text-muted-foreground w-full bg-transparent text-base outline-none md:text-[13.5px]"
                    onChange={(event) => onSearchChange(event.target.value)}
                />
            </div>

            <ToggleGroup
                value={[filters.action]}
                onValueChange={(value: string[]) => {
                    if (value.length > 0) {
                        onActionChange(value[0] as AuditActionFilter);
                    }
                }}
                spacing={0.5}
                className={`bg-paper rounded-[10px] p-1 ${TOOLBAR_SHADOW}`}
            >
                {ACTION_SEGMENTS.map((segment) => (
                    <ToggleGroupItem
                        key={segment.value}
                        value={segment.value}
                        className="text-grey-500 hover:text-fg data-pressed:text-fg new:data-pressed:bg-segmented new:rounded-sm h-auto rounded-[7px] border-0 bg-transparent px-3.25 py-1.5 text-[12.5px] font-medium hover:bg-transparent data-pressed:bg-[#F1F1F1] data-pressed:font-semibold"
                    >
                        {t(segment.labelKey)}
                    </ToggleGroupItem>
                ))}
            </ToggleGroup>

            <EntityTypeFilter entityTypes={entityTypes} selected={filters.entityTypes} onChange={onEntityTypesChange} />
            <AuthorFilter selected={filters.authorIds} onChange={onAuthorIdsChange} />
            <TimeFilter
                range={{ from: filters.changedAtFrom, to: filters.changedAtTo }}
                onChange={onChangedAtRangeChange}
            />
            <label className="text-grey-500 hover:text-fg flex h-10 cursor-pointer items-center gap-2 px-1 text-[12.5px] font-medium whitespace-nowrap select-none">
                <Switch checked={filters.hideSystem} onCheckedChange={onHideSystemChange} />
                {t('audit-trail.hide-system-events')}
            </label>

            {filters.transactionId && (
                <div className="new:bg-primary-wash new:shadow-[0_0_0_1px_var(--primary-wash-border)] flex h-10 items-center gap-2 rounded-[10px] bg-[#EDF3ED] pr-1.5 pl-3.5 shadow-[0_0_0_1px_#CFE0CE]">
                    <ListIcon className="new:text-primary-ink size-3.25 flex-none text-[#4B7049]" />
                    <span className="text-primary font-mono text-[12.5px] font-medium">{filters.transactionId}</span>
                    <button
                        type="button"
                        title={t('audit-trail.clear-transaction-filter')}
                        className="new:bg-primary-tint new:text-primary-ink new:hover:bg-primary-wash-border flex size-5.5 cursor-pointer items-center justify-center rounded-md bg-[#DCE8DB] text-[#4B7049] hover:bg-[#CFE0CE]"
                        onClick={onClearTransaction}
                    >
                        <XIcon className="size-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
